import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
    ArrowLeft, Coffee, Loader2, MessageCircle, Pin, Plus, Send, Store, Trash2, Users,
} from "lucide-react";
import Layout from "@/components/layout/Layout";
import { PageIntro } from "@/components/layout/PageIntro";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useMall } from "@/context/MallContext";

const AMBER = "#C4801A";
const when = (iso) => {
    if (!iso) return "";
    try {
        const d = new Date(iso);
        const mins = Math.round((Date.now() - d.getTime()) / 60000);
        if (mins < 1) return "just now";
        if (mins < 60) return `${mins}m ago`;
        if (mins < 1440) return `${Math.round(mins / 60)}h ago`;
        return d.toLocaleDateString([], { month: "short", day: "numeric" });
    } catch { return ""; }
};

/** The shop you are in keeps its color. Everyone else on the community board is black and white. */
function Who({ author, className = "" }) {
    const { store: active } = useMall();
    const mine = active?.slug && (author?.store_slug === active.slug || author?.store_name === active.name);
    return (
        <span className={`inline-flex flex-wrap items-center gap-1.5 ${className}`}>
            <span className="text-sm font-bold text-zinc-900">{author?.name || "Someone"}</span>
            {author?.store_name && (
                <span className="inline-flex items-center gap-1 rounded border border-black/12 bg-white px-1.5 py-0.5 font-mono text-[11px] font-bold uppercase tracking-[0.1em] text-zinc-800">
                    <span className="h-2 w-2 rounded-sm" style={{ background: mine ? (author.theme || "#71717a") : "#71717a" }} />
                    {author.store_name}
                </span>
            )}
            {author?.is_admin && (
                <span className="rounded bg-zinc-900 px-1.5 py-0.5 font-mono text-[11px] font-bold uppercase tracking-[0.1em] text-zinc-50">
                    13thav support
                </span>
            )}
        </span>
    );
}

export default function FoodCourt() {
    const { user } = useAuth();
    const [data, setData] = useState(null);
    const [room, setRoom] = useState("");
    const [open, setOpen] = useState(null);
    const [busy, setBusy] = useState(false);
    const [reply, setReply] = useState("");
    const [draft, setDraft] = useState({ room: "shop-talk", title: "", body: "" });
    const [composing, setComposing] = useState(false);

    const load = useCallback(() => {
        api.get("/community", { params: room ? { room } : {} }).then(({ data }) => setData(data)).catch(() => setData({ rooms: [], threads: [] }));
    }, [room]);
    useEffect(() => { load(); }, [load]);

    const openThread = async (id) => {
        try { const { data: res } = await api.get(`/community/threads/${id}`); setOpen(res.thread); }
        catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    };

    const post = async () => {
        if (!draft.title.trim()) { toast.error("Give it a headline"); return; }
        setBusy(true);
        try {
            const { data: res } = await api.post("/community/threads", draft);
            toast.success(res.note);
            setDraft({ room: draft.room, title: "", body: "" });
            setComposing(false);
            setOpen(res.thread);
            load();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
        finally { setBusy(false); }
    };

    const send = async () => {
        if (!reply.trim()) return;
        setBusy(true);
        try {
            const { data: res } = await api.post(`/community/threads/${open.id}/replies`, { body: reply });
            setOpen(res.thread); setReply(""); load();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
        finally { setBusy(false); }
    };

    const drop = async (id) => {
        try { await api.delete(`/community/threads/${id}`); toast.success("Post removed"); setOpen(null); load(); }
        catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    };

    const pin = async (id) => {
        try { await api.post(`/community/threads/${id}/pin`); load(); }
        catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    };

    const rooms = data?.rooms || [];
    const threads = data?.threads || [];

    return (
        <Layout>

            <PageIntro trail={[{ label: "Community" }]} kicker="Plaza · the community board" title="The community board." testid="food-court-hero"
                description="The chat room for everybody on 13thav. Shops help shops, customers ask questions, everybody says what worked and what didn't — and what this site should do next. Read it without an account; log in to talk."
                right={
                    <p className="flex flex-wrap items-center gap-x-5 gap-y-1 font-mono text-[11px] uppercase tracking-[0.16em] text-zinc-700">
                        <span className="inline-flex items-center gap-1.5"><MessageCircle size={12} /> {data?.totals?.threads ?? 0} conversations</span>
                        <span className="inline-flex items-center gap-1.5"><Users size={12} /> {data?.totals?.replies ?? 0} replies</span>
                        {!user && <Link to="/login" className="font-bold text-zinc-900 hover:underline" data-testid="food-court-login">log in to post →</Link>}
                    </p>
                } />

            <section className="mx-auto max-w-7xl px-4 py-8 sm:px-6">
                <div className="grid gap-5 lg:grid-cols-[220px_minmax(0,1fr)]">
                    <nav data-testid="food-court-rooms" className="space-y-1">
                        <button onClick={() => { setRoom(""); setOpen(null); }} data-testid="food-court-room-all"
                            className={`flex w-full items-center gap-2 rounded border-l-2 px-2.5 py-1.5 text-left text-sm ${!room
                                ? "border-l-[#C4801A] bg-zinc-100 font-semibold text-zinc-900"
                                : "border-transparent text-zinc-700 hover:bg-zinc-50 hover:text-zinc-900"}`}>
                            <Coffee size={14} /> Every table
                            <span className="ml-auto font-mono text-[11px] tabular-nums text-zinc-700">{data?.totals?.threads ?? 0}</span>
                        </button>
                        {rooms.map((r) => (
                            <button key={r.key} onClick={() => { setRoom(r.key); setOpen(null); }}
                                data-testid={`food-court-room-${r.key}`}
                                className={`flex w-full items-center gap-2 rounded border-l-2 px-2.5 py-1.5 text-left text-sm ${room === r.key
                                    ? "border-l-[#C4801A] bg-zinc-100 font-semibold text-zinc-900"
                                    : "border-transparent text-zinc-700 hover:bg-zinc-50 hover:text-zinc-900"}`}>
                                <span className="truncate">{r.label}</span>
                                <span className="ml-auto font-mono text-[11px] tabular-nums text-zinc-700">{r.threads}</span>
                            </button>
                        ))}
                        {user ? (
                            <button onClick={() => { setComposing(true); setOpen(null); }} data-testid="food-court-new"
                                className="mt-2 flex w-full items-center gap-2 rounded-full bg-zinc-900 px-3.5 py-2 font-mono text-[11px] font-bold uppercase tracking-[0.14em] text-zinc-50 hover:bg-black">
                                <Plus size={12} /> start a table
                            </button>
                        ) : (
                            <Link to="/login" className="mt-2 flex w-full items-center gap-2 rounded-full border border-black/15 bg-white px-3.5 py-2 font-mono text-[11px] font-bold uppercase tracking-[0.14em] text-zinc-800 hover:border-zinc-900">
                                <Plus size={12} /> log in to post
                            </Link>
                        )}
                        <p className="pt-3 text-xs text-zinc-700">
                            Shop owners: everything you post and every reply you get shows up in your
                            {" "}<Link to="/account/inbox" className="font-semibold underline">back-office inbox</Link>.
                        </p>
                    </nav>

                    <div>
                        {composing && (
                            <div className="mb-4 rounded-lg border border-black/10 bg-white p-4" data-testid="food-court-composer">
                                <p className="font-mono text-[11px] font-bold uppercase tracking-[0.18em]" style={{ color: AMBER }}>
                                    Start a table
                                </p>
                                <div className="mt-2.5 flex flex-wrap gap-1.5">
                                    {rooms.map((r) => (
                                        <button key={r.key} onClick={() => setDraft((d) => ({ ...d, room: r.key }))}
                                            data-testid={`composer-room-${r.key}`}
                                            className={`rounded-full border px-3 py-1 font-mono text-[11px] font-bold uppercase tracking-[0.12em] ${draft.room === r.key
                                                ? "border-[#1a1a1a] bg-[#1a1a1a] text-white bo-inv" : "border-[#d0d0d0] bg-white text-[#1a1a1a] hover:border-[#1a1a1a]"}`}>
                                            {r.label}
                                        </button>
                                    ))}
                                </div>
                                <input className="mt-3 w-full rounded-lg border border-black/15 bg-white px-3 py-2.5 text-sm text-zinc-900 outline-none placeholder:text-zinc-500 focus:border-zinc-900"
                                    placeholder="What's the question?" value={draft.title} data-testid="composer-title"
                                    onChange={(e) => setDraft((d) => ({ ...d, title: e.target.value }))} />
                                <textarea className="mt-2 w-full rounded-lg border border-black/15 bg-white px-3 py-2.5 text-sm text-zinc-900 outline-none placeholder:text-zinc-500 focus:border-zinc-900"
                                    rows={4} placeholder="Say a bit more…" value={draft.body} data-testid="composer-body"
                                    onChange={(e) => setDraft((d) => ({ ...d, body: e.target.value }))} />
                                <div className="mt-2.5 flex flex-wrap items-center gap-2">
                                    <button onClick={post} disabled={busy} data-testid="composer-post"
                                        className="inline-flex items-center gap-1.5 rounded-full bg-zinc-900 px-4 py-2 font-mono text-[11px] font-bold uppercase tracking-[0.14em] text-zinc-50 hover:bg-black disabled:opacity-50">
                                        {busy ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />} Post it
                                    </button>
                                    <button onClick={() => setComposing(false)} data-testid="composer-cancel"
                                        className="font-mono text-[11px] font-bold uppercase tracking-[0.14em] text-zinc-600 hover:text-zinc-900">cancel</button>
                                </div>
                            </div>
                        )}

                        {open ? (
                            <div className="rounded-lg border border-black/10 bg-white" data-testid="food-court-thread">
                                <div className="flex flex-wrap items-center gap-3 border-b border-black/10 bg-zinc-100 px-4 py-2.5">
                                    <button onClick={() => setOpen(null)} data-testid="thread-back"
                                        className="inline-flex items-center gap-1.5 font-mono text-[11px] font-bold uppercase tracking-[0.14em] text-zinc-700 hover:text-zinc-900">
                                        <ArrowLeft size={12} /> all tables
                                    </button>
                                    <span className="font-mono text-[11px] font-bold uppercase tracking-[0.14em] text-zinc-800">
                                        {rooms.find((r) => r.key === open.room)?.label || open.room}
                                    </span>
                                    {data?.is_admin && (
                                        <>
                                            <button onClick={() => pin(open.id)} data-testid="thread-pin"
                                                className="ml-auto inline-flex items-center gap-1 font-mono text-[11px] font-bold uppercase tracking-[0.12em] text-zinc-800 hover:text-zinc-900">
                                                <Pin size={11} /> {open.pinned ? "unpin" : "pin"}
                                            </button>
                                            <button onClick={() => drop(open.id)} data-testid="thread-delete"
                                                className="text-zinc-600 hover:text-red-600"><Trash2 size={13} /></button>
                                        </>
                                    )}
                                    {!data?.is_admin && open.mine && (
                                        <button onClick={() => drop(open.id)} data-testid="thread-delete"
                                            className="ml-auto text-zinc-600 hover:text-red-600"><Trash2 size={13} /></button>
                                    )}
                                </div>
                                <div className="p-4">
                                    <h2 className="font-display text-xl font-black tracking-tight text-zinc-900">{open.title}</h2>
                                    <p className="mt-1.5"><Who author={open.author} /> <span className="font-mono text-[11px] uppercase tracking-[0.12em] text-zinc-600">· {when(open.created_at)}</span></p>
                                    {open.body && <p className="mt-3 whitespace-pre-line border-l-2 border-black/10 pl-3.5 text-sm text-zinc-900">{open.body}</p>}

                                    <div className="mt-4 space-y-2.5 border-t border-black/10 pt-3" data-testid="thread-replies">
                                        {(open.thread || []).map((r) => (
                                            <div key={r.id} className="rounded border border-black/10 bg-zinc-50 px-3 py-2" data-testid={`reply-${r.id}`}>
                                                <p><Who author={r.author} /> <span className="font-mono text-[11px] uppercase tracking-[0.12em] text-zinc-600">· {when(r.created_at)}</span></p>
                                                <p className="mt-1 whitespace-pre-line text-sm text-zinc-900">{r.body}</p>
                                            </div>
                                        ))}
                                        {!(open.thread || []).length && (
                                            <p className="text-sm text-zinc-700" data-testid="thread-no-replies">Nobody has answered yet. Be first.</p>
                                        )}
                                    </div>

                                    {user ? (
                                        <div className="mt-3 border-t border-black/10 pt-3">
                                            <textarea className="w-full rounded-lg border border-black/15 bg-white px-3 py-2.5 text-sm text-zinc-900 outline-none placeholder:text-zinc-500 focus:border-zinc-900"
                                                rows={3} placeholder="Answer, or add what you know…" value={reply}
                                                onChange={(e) => setReply(e.target.value)} data-testid="reply-input" />
                                            <button onClick={send} disabled={busy || !reply.trim()} data-testid="reply-send"
                                                className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-zinc-900 px-4 py-2 font-mono text-[11px] font-bold uppercase tracking-[0.14em] text-zinc-50 hover:bg-black disabled:opacity-50">
                                                {busy ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />} Reply
                                            </button>
                                        </div>
                                    ) : (
                                        <p className="mt-3 border-t border-black/10 pt-3 text-sm text-zinc-800">
                                            <Link to="/login" className="font-semibold underline">Log in</Link> to answer this one.
                                        </p>
                                    )}
                                </div>
                            </div>
                        ) : !data ? (
                            <div className="flex justify-center py-16"><Loader2 className="animate-spin text-zinc-500" /></div>
                        ) : threads.length ? (
                            <div className="overflow-hidden rounded-lg border border-black/10 bg-white" data-testid="food-court-threads">
                                {threads.map((t) => (
                                    <button key={t.id} onClick={() => openThread(t.id)} data-testid={`thread-row-${t.id}`}
                                        className="flex w-full flex-wrap items-center gap-x-3 gap-y-1 border-t border-black/10 px-4 py-2.5 text-left first:border-t-0 hover:bg-zinc-50">
                                        {t.pinned && <Pin size={11} style={{ color: AMBER }} data-testid={`thread-pinned-${t.id}`} />}
                                        <span className="min-w-[200px] flex-1">
                                            <span className="block text-sm font-bold text-zinc-900">{t.title}</span>
                                            <span className="mt-0.5 block font-mono text-[11px] uppercase tracking-[0.1em] text-zinc-700">
                                                {t.author?.store_name || t.author?.name} · {rooms.find((r) => r.key === t.room)?.label || t.room}
                                            </span>
                                        </span>
                                        <span className="inline-flex items-center gap-1 font-mono text-[11px] font-bold uppercase tracking-[0.1em] text-zinc-800">
                                            <MessageCircle size={11} /> {t.replies}
                                        </span>
                                        <span className="font-mono text-[11px] uppercase tracking-[0.1em] text-zinc-600">{when(t.last_at)}</span>
                                    </button>
                                ))}
                            </div>
                        ) : (
                            <div className="rounded-lg border border-dashed border-black/15 bg-white p-10 text-center" data-testid="food-court-empty">
                                <Store size={28} className="mx-auto text-zinc-400" />
                                <p className="mt-3 text-sm text-zinc-800">
                                    Quiet in here. Start the first table — ask something, or tell everybody what sold this week.
                                </p>
                                {user ? (
                                    <button onClick={() => setComposing(true)} data-testid="food-court-empty-new"
                                        className="mt-3 font-mono text-[11px] font-bold uppercase tracking-[0.16em] text-zinc-900 hover:underline">
                                        start a table →
                                    </button>
                                ) : (
                                    <Link to="/login" className="mt-3 inline-block font-mono text-[11px] font-bold uppercase tracking-[0.16em] text-zinc-900 hover:underline">
                                        log in to post →
                                    </Link>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </section>
        </Layout>
    );
}
